import { useMemo, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { toast } from "@/v2-ui/toast"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import { LICENSE_STATUS_TRIAL } from "@/v2-backoffice-client/license/license.constants"
import { useLicenseQuery } from "@/v2-backoffice-client/license/license.query"
import { useLicenseSeatHoldersByCustomerIds } from "@/v2-backoffice-client/license/seats/license.seats.holders.hooks"
import { usePortfolioQuery } from "@/v2-backoffice-client/portfolio/portfolio.query"
import { usePortfolioDailyData } from "@/v2-backoffice-client/portfolio/portfolio.daily.data"
import { usePortfolioTimeframe } from "@/v2-backoffice-client/portfolio/portfolio.filters"
import PortfolioActivityHero from "@/v2-backoffice-client/portfolio/PortfolioActivityHero"
import PortfolioEngagementBands from "@/v2-backoffice-client/portfolio/PortfolioEngagementBands"
import {
  ACTIVITY_VIEW_TRIALS,
  buildActivityLink,
} from "@/v2-backoffice-client/signIn/signIn.constants"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import { deriveUsageScopeSummary } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import LicenseTrialKpis from "@/v2-backoffice-client/license/trials/LicenseTrialKpis"
import LicenseTrialTable from "@/v2-backoffice-client/license/trials/LicenseTrialTable"
import { TRIALS_WINDOW_PARAM } from "@/v2-backoffice-client/license/trials/license.trials.constants"
import { formatTrialSummary } from "@/v2-backoffice-client/license/trials/license.trials.summary"
import {
  deriveTrialCustomers,
  deriveTrialKpis,
} from "@/v2-backoffice-client/license/trials/license.trials.utils"

export type LicenseTrialReportProps = ComponentProps<"div">

const EMPTY_LICENSES: never[] = []
const EMPTY_PORTFOLIO: never[] = []

/**
 * The trials report: who is on trial, on what, how long is left, and whether
 * anyone uses it — the weekly hand-made post as a page that is always
 * current and linkable. Trial licenses come from the license domain (every
 * product, the seated MCP servers included); the people and their activity
 * from the same portfolio read and derivations the Activity tabs use, so a
 * number here matches the customer's own panel.
 */
function LicenseTrialReport(props: LicenseTrialReportProps) {
  const { className, ...divProps } = props

  const {
    data: licenseData,
    loading: licensesLoading,
    error: licensesError,
    refetch: refetchLicenses,
  } = useLicenseQuery({
    variables: { licenseStatusCode: LICENSE_STATUS_TRIAL },
    notifyOnNetworkStatusChange: true,
  })
  const {
    data: portfolioData,
    loading: portfolioLoading,
    error: portfolioError,
    refetch: refetchPortfolio,
  } = usePortfolioQuery()

  const licenses = licenseData?.getLicenses ?? EMPTY_LICENSES
  const portfolio = portfolioData?.getUsagePortfolio ?? EMPTY_PORTFOLIO

  // Named seat holders, only for the trials whose product assigns seats to
  // people (the MCP servers) — so an MCP pilot can say who is on it.
  const seatedCustomerIds = useMemo(
    () =>
      licenses
        .filter((license) => license.SeatsAreAssigned)
        .map((license) => license.CustomerId),
    [licenses],
  )
  const seatHolders = useLicenseSeatHoldersByCustomerIds(seatedCustomerIds)

  // The derivation walks every seat of every trialing customer and feeds
  // the table, the tiles and the charts: computed once per data change.
  const customers = useMemo(
    () => deriveTrialCustomers({ licenses, portfolio, seatHolders }),
    [licenses, portfolio, seatHolders],
  )
  const kpis = useMemo(() => deriveTrialKpis(customers), [customers])
  const cohort = useMemo(
    () =>
      deriveUsageScopeSummary(
        customers.flatMap((customer) => customer.members),
      ),
    [customers],
  )

  // The cohort's daily activity, windowed like the Portfolio's hero chart
  // and scoped server-side to the trialing customers. Held until the
  // cohort is known; with nobody in it there is nothing to draw.
  const timeframe = usePortfolioTimeframe(TRIALS_WINDOW_PARAM)
  const cohortCustomerIds = useMemo(
    () =>
      customers
        .map((customer) => customer.externalCustomerId)
        .filter((id): id is string => id != null),
    [customers],
  )
  const daily = usePortfolioDailyData({
    timeWindow: timeframe.window,
    customerIds: cohortCustomerIds,
    excludeCustomerIds: [],
    excludeUserIds: [],
    skip: cohortCustomerIds.length === 0,
  })

  const isLoading =
    (licensesLoading && licenses.length === 0) ||
    (portfolioLoading && portfolio.length === 0)
  // The rows' own reads; the chart's read failing is shown by the header
  // banner, not by emptying the table.
  const tableError = licensesError ?? portfolioError
  const error = tableError ?? daily.error

  function refetch() {
    void refetchLicenses()
    void refetchPortfolio()
  }

  async function copySummary() {
    const reportUrl = `${window.location.origin}${buildActivityLink(ACTIVITY_VIEW_TRIALS)}`
    const text = formatTrialSummary({ customers, kpis, reportUrl })
    try {
      await navigator.clipboard.writeText(text)
      toast({
        title: "Weekly summary copied",
        description:
          "The key figures, the trials ending or overdue, the new ones and the quiet ones — with a link back here. Paste it into Slack.",
      })
    } catch {
      toast({
        variant: "destructive",
        title: "Could not copy the summary",
        description: "The browser refused clipboard access.",
      })
    }
  }

  return (
    <div
      className={cn("license-trial-report usage-report", className)}
      {...divProps}
    >
      <UsageReportSection
        eyebrow="Trials"
        title="Who is on trial, and whether they use it"
        lead="Every customer with a trial license — Placepoint, Propcloud, an MCP server — with how long is left and what the people on it actually do. Live from the license domain and the usage profiles, so this page is the weekly trial update."
        info="A customer is on trial when it holds a license with status Trial, on any product; an MCP pilot counts even where the customers board's governing status does not show it. People are the customer's users (every seat), their activity the nightly usage profiles over the last 28 days. Engaged: someone active in the last 7 days. Cooling: activity in 28 days, nobody in 7. Silent: no activity in 28 days. The clock reads the soonest-ending trial license."
        aside={
          <div className="license-trial-report-actions">
            <TooltipButton
              variant="secondary"
              size="sm"
              iconSrc="copy"
              content="Copy the week's summary as text — the figures, who is ending, overdue, new and quiet, plus a link to this page — ready to paste into Slack."
              onClick={() => {
                void copySummary()
              }}
              disabled={isLoading || customers.length === 0}
            >
              Copy weekly summary
            </TooltipButton>
          </div>
        }
      >
        <ErrorMessage error={error} />
        {/* The tiles wait for the data: zeros while loading would read as
            "no trials" for the seconds the two reads take. */}
        {!isLoading && <LicenseTrialKpis kpis={kpis} />}
      </UsageReportSection>

      {customers.length > 0 && (
        <div className="grid items-start gap-x-8 gap-y-9 xl:grid-cols-3">
          <PortfolioActivityHero
            className="xl:col-span-2"
            areaDays={daily.areaDays}
            timeWindow={timeframe.window}
            timeframeKey={timeframe.key}
            onTimeframeChange={timeframe.setKey}
          />
          <PortfolioEngagementBands
            bands={cohort.seatBands}
            lead="Every seat at a trialing customer, banded by how engaged it was in the last 28 days."
            info="Every user of every trialing customer, banded by engagement over the last 28 days. On a trial the idle band is the conversion risk: seats nobody has used will not be bought."
          />
        </div>
      )}

      <UsageReportSection
        eyebrow="Customers"
        title="Every trial, one row each"
        lead="Soonest-deciding clock first. Click a row for the people on the trial and what each of them does; a customer name opens its Activity tab. The pills are the worklists; owner and product narrow the list; the menu exports it."
      >
        <LicenseTrialTable
          customers={customers}
          isLoading={isLoading}
          error={tableError ?? null}
          onRefresh={refetch}
        />
      </UsageReportSection>
    </div>
  )
}

export default LicenseTrialReport
